import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Outlet } from 'react-router-dom';
import { useAppContainerContext } from './AppContainer';
import { useVerifyToken } from '../hooks/useVerifyToken';
import { LoadingView } from './LoadingView';
import { useInvitationMachine } from '../hooks/useInvitationMachine';
import {
    acceptInvitation,
    isTokenExpired,
    acceptProductInvitation,
    InvitationNextStep,
} from '../api';
import { accessToken } from '../utils/auth';
import { matchHostname } from '@lendica/utils';

const buildInviteUrl = (path: string, token?: string, returnTo?: string) => {
    const qp = new URLSearchParams();
    if (token) qp.set('token', token);
    if (returnTo) qp.set('returnTo', returnTo);
    const qs = qp.toString();
    return qs ? `${path}?${qs}` : path;
};

export const VerifyInvitationController = () => {
    const navigate = useNavigate();
    const verifyToken = useVerifyToken();
    const { setPageLoading, setSuccessMsg, setError, updateLoginValue } = useAppContainerContext();
    const { state } = useInvitationMachine(verifyToken);
    const [shouldErrorRedirect, setShouldErrorRedirect] = useState(false);

    useEffect(() => {
        setPageLoading(true);
        setSuccessMsg('Verifying your invitation...');
        if (!verifyToken) {
            const timeoutId = setTimeout(() => setShouldErrorRedirect(true), 300);
            return () => {
                clearTimeout(timeoutId);
            };
        }
        updateLoginValue({ verifyToken });
    }, []);

    useEffect(() => {
        const email = state.context.email;
        if (email) {
            updateLoginValue({ email });
        }
    }, [state.context.email]);

    useEffect(() => {
        let returnTo: string | undefined;
        if (!verifyToken && shouldErrorRedirect) {
            setPageLoading(false);
            navigate('error', { replace: true });
            return;
        }
        switch (state.value) {
            case InvitationNextStep.Login: {
                setSuccessMsg('Please login to continue.');
                returnTo = `../accept?token=${verifyToken}`;
                navigate(buildInviteUrl('signin', verifyToken, returnTo), {
                    replace: true,
                    state: { returnTo },
                });
                setTimeout(() => setSuccessMsg(null), 2500);
                break;
            }
            case InvitationNextStep.SignUp:
                navigate(buildInviteUrl('signup', verifyToken), {
                    replace: true,
                });
                break;
            case InvitationNextStep.LogoutAndSignUp:
                returnTo = `../signup?token=${verifyToken}`;
                navigate(buildInviteUrl('logout-and-signup', verifyToken, returnTo), {
                    replace: true,
                    state: { returnTo },
                });
                break;
            case InvitationNextStep.Accept:
                navigate(buildInviteUrl('accept', verifyToken), { replace: true });
                break;
            case InvitationNextStep.SwitchAccount:
                returnTo = `../accept?token=${verifyToken}`;
                navigate(buildInviteUrl('switch-account', verifyToken, returnTo), {
                    replace: true,
                    state: { returnTo },
                });
                break;
            case InvitationNextStep.Error:
                setError('Invalid or expired invitation link.');
                navigate('error', { replace: true });
                break;
            default:
                break;
        }
        setSuccessMsg(null);
        setPageLoading(false);
    }, [state.value, verifyToken, shouldErrorRedirect]);
    return <Outlet />;
};

export const AcceptInvitationController = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const tokenFromQuery = searchParams.get('token') || undefined;
    const { setSuccessMsg, setError, loginValue } = useAppContainerContext();

    const [shouldErrorRedirect, setShouldErrorRedirect] = useState(false);

    useEffect(() => {
        if (tokenFromQuery || loginValue.verifyToken) return;
        const timeoutId = setTimeout(() => setShouldErrorRedirect(true), 300);
        return () => clearTimeout(timeoutId);
    }, [tokenFromQuery, loginValue.verifyToken]);

    useEffect(() => {
        const verifyToken = tokenFromQuery || loginValue.verifyToken;

        if (!verifyToken) {
            if (shouldErrorRedirect) {
                navigate('../error', { replace: true });
            }
            return;
        }

        const authed = !!accessToken.get() && !isTokenExpired();
        if (!authed) {
            const returnTo = `../accept?token=${verifyToken}`;
            navigate(buildInviteUrl('../signin', verifyToken, returnTo), {
                replace: true,
                state: { returnTo },
            });
            return;
        }

        (async () => {
            try {
                const res = await acceptInvitation(verifyToken);
                setSuccessMsg(res.message || 'Invitation accepted');
                setTimeout(() => navigate('/', { replace: true }), 2000);
            } catch (e) {
                setError('Unable to accept invitation');
                setTimeout(() => navigate('/', { replace: true }), 1200);
            }
        })();
    }, [tokenFromQuery, loginValue.verifyToken, shouldErrorRedirect]);
    return <LoadingView />;
};

export const ProductAcceptInvitationController = () => {
    const navigate = useNavigate();
    const verifyToken = useVerifyToken();
    const { setPageLoading, setSuccessMsg, setError, updateLoginValue } = useAppContainerContext();

    const [shouldErrorRedirect, setShouldErrorRedirect] = useState(false);

    const { state } = useInvitationMachine(verifyToken, acceptProductInvitation);

    useEffect(() => {
        setPageLoading(true);
        setSuccessMsg('Verifying your invitation...');
        if (!verifyToken) {
            const timeoutId = setTimeout(() => setShouldErrorRedirect(true), 300);
            return () => {
                clearTimeout(timeoutId);
            };
        }
        updateLoginValue({ verifyToken });
    }, []);

    useEffect(() => {
        const email = state.context.email;
        if (email) {
            updateLoginValue({ email });
        }
    }, [state.context.email]);

    useEffect(() => {
        if (!verifyToken && shouldErrorRedirect) {
            setPageLoading(false);
            navigate('error', { replace: true });
            return;
        }

        let timeoutId: ReturnType<typeof setTimeout> | undefined;

        switch (state.value) {
            case InvitationNextStep.SignUp:
                setSuccessMsg(null);
                window.location.href = `https://apply.${matchHostname()}?token=${verifyToken}&email=${encodeURIComponent(
                    state.context.email || ''
                )}&utm_source=product-invite&utm_medium=portal`;
                break;
            case InvitationNextStep.LogoutAndSignUp:
                setSuccessMsg('Please sign out to continue.');
                navigate('logout-and-apply', { replace: true });
                timeoutId = setTimeout(() => {
                    setSuccessMsg(null);
                }, 1500);
                break;
            case InvitationNextStep.Login:
                setSuccessMsg('Please login to continue.');
                navigate('/', { replace: true });
                timeoutId = setTimeout(() => {
                    setSuccessMsg(null);
                }, 1500);
                break;
            case InvitationNextStep.SwitchAccount:
                setSuccessMsg('Please switch account to continue.');
                navigate('switch-account', { replace: true });
                timeoutId = setTimeout(() => {
                    setSuccessMsg(null);
                }, 1500);
                break;
            case InvitationNextStep.Accept: {
                const message = state.context.message || 'Invitation accepted';
                setSuccessMsg(message);
                timeoutId = setTimeout(() => {
                    setSuccessMsg(null);
                    navigate('/', { replace: true });
                }, 1500);
                break;
            }
            case InvitationNextStep.Error:
            case 'error':
                setSuccessMsg(null);
                setError('Invalid or expired invitation link.');
                timeoutId = setTimeout(() => {
                    setError(null);
                    navigate('error', { replace: true });
                }, 1200);
                break;
            default:
                break;
        }
        setPageLoading(false);

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [state.value, verifyToken, shouldErrorRedirect]);

    return <Outlet />;
};
